import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Video,
  Clock,
  User,
  Plus,
  Play,
  Star,
  TrendingUp,
  Home,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCourses, getMyEnrollments, enrollCourse } from '../../services/api';

const TradingViewCoursesLayout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    if (user) {
      fetchTradingViewCourses();
      fetchEnrollments();
    }
  }, [user]);

  const fetchTradingViewCourses = async () => {
    try {
      setLoading(true);
      const response = await getCourses({
        courseType: 'video',
        category: 'tradingview',
        limit: 50
      });

      let sortedCourses = response.data.courses || [];

      // Sort courses
      switch (sortBy) {
        case 'newest':
          sortedCourses.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'popular':
          sortedCourses.sort((a, b) => (b.enrollmentCount || 0) - (a.enrollmentCount || 0));
          break;
        case 'duration':
          sortedCourses.sort((a, b) => {
            const durationA = parseFloat(a.totalDuration) || 0;
            const durationB = parseFloat(b.totalDuration) || 0;
            return durationA - durationB;
          });
          break;
        default:
          break;
      }

      setCourses(sortedCourses);
    } catch (error) {
      console.error('Error fetching TradingView courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const response = await getMyEnrollments({ courseType: 'video' });
      setEnrollments(response.data.enrollments || []);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      await enrollCourse(courseId);
      fetchEnrollments();
    } catch (error) {
      console.error('Error enrolling in course:', error);
      alert('Failed to enroll in course');
    }
  };

  const isEnrolled = (courseId) => {
    return enrollments.some((enrollment) =>
      enrollment.course?._id === courseId || enrollment.course === courseId
    );
  };

  const getEnrollment = (courseId) => {
    return enrollments.find((enrollment) =>
      enrollment.course?._id === courseId || enrollment.course === courseId
    );
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (user) {
        fetchTradingViewCourses();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, sortBy]);

  const CourseCard = ({ course }) => {
    const enrolled = isEnrolled(course._id);
    const enrollment = getEnrollment(course._id);

    return (
      <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-200 hover:shadow-lg group">
        <div className="aspect-[16/9] overflow-hidden relative">
          <img
            src={course.thumbnail || 'https://images.unsplash.com/photo-1642790551116-18e150f248e3?auto=format&fit=crop&w=800&h=600&q=80'}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
              <Play size={24} className="text-[#30C4B5] ml-1" />
            </div>
          </div>
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center px-2 py-1 rounded bg-[#30C4B5] text-white text-xs font-medium">
              <Video size={12} className="mr-1" />
              TradingView
            </span>
          </div>
          {course.averageRating && (
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center px-2 py-1 rounded bg-yellow-500 text-white text-xs font-medium">
                <Star size={12} className="mr-1" />
                {course.averageRating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
              <TrendingUp size={12} className="mr-1" />
              Video Course
            </span>
            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              {course.totalLessons || 0} lessons
            </span>
          </div>

          <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2 line-clamp-2">
            {course.title}
          </h3>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
            {course.shortDescription}
          </p>

          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-4 space-x-4">
            <div className="flex items-center">
              <User size={12} className="mr-1" />
              {course.instructor?.firstName || 'Instructor'}
            </div>
            <div className="flex items-center">
              <Clock size={12} className="mr-1" />
              {course.totalDuration || '0h'}
            </div>
            <div className="flex items-center">
              <Video size={12} className="mr-1" />
              HD Quality
            </div>
          </div>

          {enrolled ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Progress
                </span>
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {enrollment?.progressPercentage || 0}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#30C4B5] rounded-full transition-all duration-300"
                  style={{ width: `${enrollment?.progressPercentage || 0}%` }}
                />
              </div>
              <button
                onClick={() => navigate(`/courses/${course._id}`)}
                className="w-full py-2 bg-[#30C4B5] hover:bg-[#29AF9F] text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center"
              >
                <Play size={16} className="mr-2" />
                Continue Learning
              </button>
            </div>
          ) : (
            <button
              onClick={() => handleEnroll(course._id)}
              className="w-full py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center group"
            >
              <Plus size={16} className="mr-2 group-hover:scale-110 transition-transform" />
              Enroll Now
            </button>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#000000]">
      <main className="max-w-[1400px] mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-4">
            <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors flex items-center">
              <Home size={14} className="mr-1" />
              Dashboard
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <Link to="/courses" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors">
              Courses
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <Link to="/video-courses" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors">
              Video Courses
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <span className="text-[#30C4B5] font-medium">TradingView</span>
          </nav>

          {/* Title */}
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[#30C4B5] rounded-lg flex items-center justify-center flex-shrink-0">
              <Video size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-3xl text-gray-900 dark:text-white mb-1">
                TradingView Video Courses
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Master TradingView platform with comprehensive video tutorials and advanced techniques.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-[#30C4B5] rounded-lg flex items-center justify-center mr-3">
                <Video size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {courses.length}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Courses
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                <TrendingUp size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {enrollments.filter((e) => courses.some((c) => c._id === (e.course?._id || e.course))).length}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enrolled
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                <Clock size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {courses.reduce((sum, c) => {
                    const duration = parseFloat(c.totalDuration) || 0;
                    return sum + duration;
                  }, 0).toFixed(1)}h
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Content
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                <Star size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {courses.length > 0
                    ? (courses.reduce((sum, c) => sum + (c.averageRating || 0), 0) / courses.length).toFixed(1)
                    : '4.8'
                  }
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Avg Rating
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search TradingView courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
            >
              <option value="newest">Newest First</option>
              <option value="popular">Most Popular</option>
              <option value="duration">Shortest First</option>
            </select>
          </div>
        </div>

        {/* Course Categories */}
        <div className="mb-8">
          <h2 className="font-bold text-xl text-gray-900 dark:text-white mb-4">
            Popular TradingView Topics
          </h2>
          <div className="flex flex-wrap gap-3">
            {['Platform Basics', 'Chart Analysis', 'Pine Script', 'Indicators', 'Drawing Tools', 'Alerts & Notifications', 'Advanced Features', 'Strategy Testing'].map((topic) => (
              <span
                key={topic}
                className="px-4 py-2 bg-[#ffffff] dark:bg-black border border-gray-200 dark:border-gray-800 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:border-[#30C4B5] hover:text-[#30C4B5] cursor-pointer transition-all"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
                <div className="aspect-[16/9] bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : courses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <CourseCard key={course._id} course={course} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800">
            <Video size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
              No TradingView courses found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search criteria or check back later for new courses.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default TradingViewCoursesLayout;
